import { cancelTokenRequests } from '../../../utils/callReliability/token';
import React, { useEffect, useRef, useState } from 'react';
import { participantIdentity } from '../../../utils/callReliability/identity';
import BackgroundOptions from '../../BackgroundSelectionDialog/BackgroundOptions/BackgroundOptions';
import TestSpeakerButton from '../../TestSpeakerButton/TestSpeakerButton';
import DiagnosticsButton from '../../DiagnosticsButton/DiagnosticsButton';
import {
	makeStyles,
	Typography,
	Grid,
	Button,
	Theme,
	Switch,
	Tooltip,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import LocalVideoPreview from './LocalVideoPreview/LocalVideoPreview';
import SettingsMenu from './SettingsMenu/SettingsMenu';
import { Steps } from '../PreJoinScreens';
import ToggleAudioButton from '../../Buttons/ToggleAudioButton/ToggleAudioButton';
import ToggleVideoButton from '../../Buttons/ToggleVideoButton/ToggleVideoButton';
import { useAppState } from '../../../state';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { useKrispToggle } from '../../../hooks/useKrispToggle/useKrispToggle';
import SmallCheckIcon from '../../../icons/SmallCheckIcon';
import InfoIconOutlined from '../../../icons/InfoIconOutlined';

const useStyles = makeStyles((theme: Theme) => ({
	layout: {
		display: 'grid',
		gap: '20px 32px',
		gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 5fr)',
		gridTemplateAreas: `
			"title title"
			"preview actions"
			"controls actions"
			"background background"
			"extras extras"
		`,
		alignItems: 'start',
		[theme.breakpoints.down('sm')]: {
			gap: 16,
			gridTemplateColumns: 'minmax(0, 1fr)',
			gridTemplateAreas: `
				"title"
				"preview"
				"controls"
				"background"
				"actions"
				"extras"
			`,
		},
	},
	title: {
		gridArea: 'title',
		minWidth: 0,
	},
	subtitle: {
		color: theme.palette.text.secondary,
		marginTop: 4,
		[theme.breakpoints.down('sm')]: {
			display: 'none',
		},
	},
	preview: {
		gridArea: 'preview',
		borderRadius: 12,
		overflow: 'hidden',
		background: '#000',
		minWidth: 0,
	},
	controls: {
		gridArea: 'controls',
		display: 'flex',
		flexWrap: 'wrap',
		gap: 8,
		minWidth: 0,
		'& > *': {
			flex: '1 1 auto',
			minWidth: 0,
		},
		[theme.breakpoints.down('sm')]: {
			flexWrap: 'nowrap',
			'& > *': {
				flex: '1 1 0',
			},
			'& button': {
				width: '100%',
				minHeight: 64,
				padding: '6px 4px',
				fontSize: '0.875rem',
				lineHeight: 1.2,
				whiteSpace: 'normal',
			},
			'& .MuiButton-label': {
				flexDirection: 'column',
				gap: 4,
			},
			'& .MuiButton-startIcon': {
				margin: 0,
			},
		},
	},
	deviceButton: {
		border: `2px solid ${theme.palette.divider}`,
		background: theme.palette.background.paper,
		'&:hover': {
			borderColor: theme.palette.text.secondary,
			background: theme.palette.background.default,
		},
	},
	actions: {
		gridArea: 'actions',
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
		minWidth: 0,
	},
	joinButton: {
		minHeight: 52,
		fontSize: '1.0625rem',
	},
	cancelButton: {
		minHeight: 48,
	},
	alert: {
		padding: '12px 16px',
		borderRadius: 8,
		background: '#FDECEA',
		border: '1px solid #F5C2C0',
	},
	notice: {
		padding: '12px 16px',
		borderRadius: 8,
		background: '#FFF4E5',
		border: '1px solid #FFD8A8',
	},
	background: {
		gridArea: 'background',
		minWidth: 0,
		paddingTop: 4,
		borderTop: `1px solid ${theme.palette.divider}`,
		[theme.breakpoints.down('sm')]: {
			paddingTop: 0,
			borderTop: 0,
		},
	},
	sectionTitle: {
		marginTop: 12,
		marginBottom: 2,
		[theme.breakpoints.down('sm')]: {
			marginTop: 0,
		},
	},
	sectionHint: {
		color: theme.palette.text.secondary,
		marginBottom: 12,
		[theme.breakpoints.down('sm')]: {
			display: 'none',
		},
	},
	status: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 8,
		marginTop: 12,
	},
	extras: {
		gridArea: 'extras',
		minWidth: 0,
		paddingTop: 16,
		borderTop: `1px solid ${theme.palette.divider}`,
	},
	toolTipContainer: {
		display: 'flex',
		alignItems: 'center',
		'& div': {
			display: 'flex',
			alignItems: 'center',
		},
		'& svg': {
			marginLeft: '0.3em',
		},
	},
	loading: {
		minHeight: 260,
		gap: 16,
		textAlign: 'center',
	},
}));

interface DeviceSelectionScreenProps {
	name: string;
	roomName: string;
	setStep: (step: Steps) => void;
}

export default function DeviceSelectionScreen({
	name,
	roomName,
	setStep,
}: DeviceSelectionScreenProps) {
	const classes = useStyles();
	const { getToken, isFetching, isKrispEnabled, isKrispInstalled, setError } =
		useAppState();

	const {
		connect: videoConnect,
		isAcquiringLocalTracks,
		isConnecting,
		cancelConnect,
		disconnectedUnexpectedly,
		backgroundStatus,
		removeLocalVideoTrack,
		removeLocalAudioTrack,
	} = useVideoContext();
	const { toggleKrisp } = useKrispToggle();
	const disableButtons = isFetching || isAcquiringLocalTracks || isConnecting;

	const [joinError, setJoinError] = useState<Error>();
	const joining = useRef(false);
	const attempt = useRef(0);
	useEffect(
		() => () => {
			attempt.current++;
			cancelTokenRequests();
			cancelConnect?.();
		},
		[cancelConnect],
	);
	const cancel = () => {
		cancelTokenRequests();
		attempt.current++;
		joining.current = false;
		cancelConnect();
		removeLocalVideoTrack();
		removeLocalAudioTrack();
		setStep(Steps.roomNameStep);
	};
	const handleJoin = async () => {
		if (joining.current) return;
		joining.current = true;
		const current = ++attempt.current;
		setJoinError(undefined);
		setError(null);
		try {
			const { token } = await getToken(
				participantIdentity(name),
				roomName,
			);
			if (current !== attempt.current) return;
			await videoConnect(token);
		} catch (error) {
			if (current === attempt.current) {
				setError(null);
				setJoinError(error as Error);
			}
		} finally {
			if (current === attempt.current) joining.current = false;
		}
	};

	if (isFetching || isConnecting) {
		return (
			<Grid
				container
				justifyContent="center"
				alignItems="center"
				direction="column"
				className={classes.loading}
			>
				<CircularProgress variant="indeterminate" />
				<Typography variant="h6" component="p">
					Joining the call…
				</Typography>
				<Button variant="outlined" color="primary" onClick={cancel}>
					Cancel
				</Button>
			</Grid>
		);
	}

	return (
		<div className={classes.layout}>
			<div className={classes.title}>
				<Typography variant="h5" component="h1">
					{disconnectedUnexpectedly
						? 'Rejoin call'
						: 'Ready to join?'}
				</Typography>
				<Typography variant="body1" className={classes.subtitle}>
					Check your camera and microphone, then choose a background.
				</Typography>
			</div>

			<div className={classes.preview}>
				<LocalVideoPreview identity={name} />
			</div>

			<div className={classes.controls}>
				<ToggleAudioButton
					className={classes.deviceButton}
					disabled={disableButtons}
				/>
				<ToggleVideoButton
					className={classes.deviceButton}
					disabled={disableButtons}
				/>
				<TestSpeakerButton className={classes.deviceButton} />
				<SettingsMenu buttonClass={classes.deviceButton} />
			</div>

			<div className={classes.actions}>
				{joinError && (
					<div className={classes.alert}>
						<Typography role="alert" color="error">
							{joinError.message}
						</Typography>
					</div>
				)}
				{disconnectedUnexpectedly && (
					<div className={classes.notice}>
						<Typography role="status">
							The connection was lost. You can rejoin the call.
						</Typography>
					</div>
				)}
				{(joinError || disconnectedUnexpectedly) && (
					<DiagnosticsButton />
				)}
				<Button
					variant="contained"
					color="primary"
					className={classes.joinButton}
					data-cy-join-now
					onClick={handleJoin}
					disabled={
						disableButtons ||
						backgroundStatus === 'loading' ||
						joinError?.name === 'RoomEndedError'
					}
				>
					{disconnectedUnexpectedly
						? 'Rejoin call'
						: joinError
							? 'Retry'
							: 'Join Now'}
				</Button>
				<Button
					variant="outlined"
					color="primary"
					className={classes.cancelButton}
					onClick={cancel}
				>
					Cancel
				</Button>
			</div>

			<section
				className={classes.background}
				aria-labelledby="background-heading"
			>
				<Typography
					id="background-heading"
					variant="subtitle1"
					component="h2"
					className={classes.sectionTitle}
				>
					Background
				</Typography>
				<Typography variant="body2" className={classes.sectionHint}>
					Blur hides what is behind you. Tap an option to try it in
					the preview.
				</Typography>
				<BackgroundOptions layout="responsive" />
				{backgroundStatus === 'loading' && (
					<div role="status" className={classes.status}>
						<CircularProgress size={20} aria-hidden="true" />
						<Typography variant="body1" component="p">
							Preparing background…
						</Typography>
						<Button onClick={() => removeLocalVideoTrack()}>
							Continue with camera off
						</Button>
					</div>
				)}
			</section>

			{isKrispInstalled && (
				<div className={classes.extras}>
					<Grid
						container
						direction="row"
						justifyContent="space-between"
						alignItems="center"
					>
						<div className={classes.toolTipContainer}>
							<Typography variant="subtitle2">
								Noise Cancellation
							</Typography>
							<Tooltip
								title="Suppress background noise from your microphone"
								interactive
								leaveDelay={250}
								leaveTouchDelay={15000}
								enterTouchDelay={0}
							>
								<div>
									<InfoIconOutlined />
								</div>
							</Tooltip>
						</div>

						<FormControlLabel
							control={
								<Switch
									checked={!!isKrispEnabled}
									checkedIcon={<SmallCheckIcon />}
									disableRipple={true}
									onClick={toggleKrisp}
								/>
							}
							label={isKrispEnabled ? 'Enabled' : 'Disabled'}
							style={{ marginRight: 0 }}
							// Prevents <Switch /> from being temporarily enabled (and then quickly disabled) in unsupported browsers after
							// isAcquiringLocalTracks becomes false:
							disabled={isKrispEnabled && isAcquiringLocalTracks}
						/>
					</Grid>
				</div>
			)}
		</div>
	);
}
